import type { CSSProperties } from 'react';

import type { JSONContent } from '@tiptap/core';

import type {
  AlignType,
  DreamRenderElementProps,
  EmptyChildren,
  RSSFeedDataPodcast,
  RSSFeedDataPodcastAppleEpisode,
  RSSFeedDataPodcastEpisodeItem,
  RSSFeedDataPodcastItem,
  RSSFeedDataPodcastShow,
  RSSFeedDataPodcastSpotifyEpisode,
  RSSFeedDataYouTube,
  RSSFeedDataYouTubeEntry,
  SliderControlsDisplayType,
  WebTheme,
} from '@/types';

export const isYouTubeShort = (entry: RSSFeedDataYouTubeEntry) => !!entry.link?.['@href']?.includes('/shorts/');

export const YOUTUBE_VIDEO_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Videos only', value: 'videos_only' },
  { label: 'Shorts only', value: 'shorts_only' },
] as const;

export type TRSSFeedAttributes = {
  insertedFromSidebar?: boolean;
  id: string;
  url: string;
  youtubeUrl: string; // for Podcasts, their youtube channel URL allows us to link to youtube for each episode
  source: 'youtube' | 'podcast' | 'beehiiv_podcast_show' | null;
  data: RSSFeedDataYouTube | RSSFeedDataPodcast | RSSFeedDataPodcastShow;
  showCount?: number;
  isLoading?: boolean;
  isError?: boolean;
  gap?: CSSProperties['gap'];
  padding?: CSSProperties['padding'];
  columns?: string;

  // RSS Selection
  rssSelectionType?: 'latest' | 'free_selection';
  selectedRSSItems?: (RSSFeedDataYouTubeEntry | RSSFeedDataPodcastItem)[] | null;
  rssOffset?: number;
  // beehiiv podcast
  podcastShowId?: string;
  selectedPodcastShowEpisodes?: RSSFeedDataPodcastEpisodeItem[];

  // Card structure
  hasMigratedToCardStructure?: boolean;
  structureTiptapState?: JSONContent;
  structureTiptapStateDraft?: JSONContent;

  // Podcast players
  itunesId?: string | undefined;
  spotifyId?: string | undefined;

  // item card
  itemPadding?: CSSProperties['padding'];
  itemGap?: CSSProperties['gap'];
  borderRadius?: CSSProperties['borderRadius'];
  borderWidth?: CSSProperties['borderWidth'];
  borderColor?: CSSProperties['borderColor'];
  borderStyle?: CSSProperties['borderStyle'];
  backgroundColor?: CSSProperties['backgroundColor'];
  textGap?: CSSProperties['gap'];

  // YouTube
  showPlayButton?: boolean;
  playAction?: 'modal' | 'link';
  hideDate?: boolean;
  youtubeVideoFilter?: 'all' | 'videos_only' | 'shorts_only';

  // Image
  imageBorderRadius?: CSSProperties['borderRadius'];
  imageAspectRatio?: number;
  imageFit?: CSSProperties['objectFit'];

  // Texts
  titleFontSize?: CSSProperties['fontSize'];
  titleFontFamily?: CSSProperties['fontFamily'];
  titleTextColor?: CSSProperties['color'];
  titleFontWeight?: CSSProperties['fontWeight'];
  titleAlignment?: AlignType;
  titleLineClamp?: string | undefined;

  subtitleFontSize?: CSSProperties['fontSize'];
  subtitleFontFamily?: CSSProperties['fontFamily'];
  subtitleTextColor?: CSSProperties['color'];
  subtitleFontWeight?: CSSProperties['fontWeight'];
  subtitleAlignment?: AlignType;

  // Desktop Slider (Embla)
  isScrollHorizontally?: boolean;
  sliderControlsDisplayType?: SliderControlsDisplayType;
  sliderControlsTextColor?: CSSProperties['color'];
  sliderControlsBackgroundColor?: CSSProperties['backgroundColor'];
  sliderControlsBorderColor?: CSSProperties['borderColor'];

  // Mobile Slider (Embla)
  isMobileScrollHorizontally?: boolean;
  mobileSliderControlsDisplayType?: SliderControlsDisplayType;
  mobileSliderControlsTextColor?: CSSProperties['color'];
  mobileSliderControlsBackgroundColor?: CSSProperties['backgroundColor'];
  mobileSliderControlsBorderColor?: CSSProperties['borderColor'];

  // Search Input
  inputEnabled?: boolean;
  inputBorderWidth?: CSSProperties['borderWidth'];
  inputBorderColor?: CSSProperties['borderColor'];
  inputBorderStyle?: CSSProperties['borderStyle'];
  inputBorderRadius?: CSSProperties['borderRadius'];
  inputBorderFocus?: CSSProperties['borderColor'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputIconFill?: CSSProperties['color'];
  inputAlignment?: 'center' | 'left' | 'right';
  inputMaxWidth?: string;
  inputMaxHeight?: string;
  inputMarginBottom?: CSSProperties['marginBottom'];

  // Empty State
  emptyStateTextColor?: CSSProperties['color'];
  emptyStateBackgroundColor?: CSSProperties['backgroundColor'];
  emptyStateText?: string;
  emptyStatePadding?: CSSProperties['padding'];
  emptyStateFontSize?: CSSProperties['fontSize'];

  // YouTube Data API
  useYouTubeDataApi?: boolean;
};

export type TRSSFeedElement = {
  type: 'rss_feed';
  attrs: TRSSFeedAttributes;
  content: EmptyChildren;
};

export type RSSFeedElementProps = DreamRenderElementProps<TRSSFeedElement> & {
  isPreviewing?: boolean;
  rssFeedMap?: Record<string, { uuid: string; itunes_id: string }>;
};

export type RSSFeedModalProps = {
  element: TRSSFeedElement;
  isPreviewing?: boolean;
  entry?: RSSFeedDataPodcastItem;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  setEntry: (value: RSSFeedDataPodcastItem | undefined) => void;
};

export type RSSFeedEpisodeProps = {
  theme: WebTheme;
  rssFeedData?: RSSFeedDataPodcast;
  url?: string;
  rssFeedMap?: Record<string, { uuid: string; itunes_id: string }>;
  podcastEpisode?: RSSFeedDataPodcastItem;
  applePodcastEpisode?: RSSFeedDataPodcastAppleEpisode;
  spotifyPodcastEpisode?: RSSFeedDataPodcastSpotifyEpisode;
  youtubePodcastEpisode?: RSSFeedDataYouTubeEntry;
};
